<script setup lang="ts">
import { ref } from 'vue'
import { ACCEPT_ATTRIBUTE } from '@/core/files'

const emit = defineEmits<{ files: [File[]] }>()

const input = ref<HTMLInputElement | null>(null)
const dragging = ref(false)
// dragenter/dragleave fire for every child element, so a boolean flag would
// flicker as the pointer moves across the zone. Counting entries fixes it.
let depth = 0

function onDragEnter(event: DragEvent): void {
  event.preventDefault()
  depth++
  dragging.value = true
}

function onDragLeave(event: DragEvent): void {
  event.preventDefault()
  depth = Math.max(0, depth - 1)
  if (depth === 0) dragging.value = false
}

function onDrop(event: DragEvent): void {
  event.preventDefault()
  depth = 0
  dragging.value = false
  const files = Array.from(event.dataTransfer?.files ?? [])
  if (files.length) emit('files', files)
}

function onPick(event: Event): void {
  const target = event.target as HTMLInputElement
  const files = Array.from(target.files ?? [])
  if (files.length) emit('files', files)
  // Reset so picking the same file twice still fires a change event.
  target.value = ''
}
</script>

<template>
  <div
    class="rounded-xl border-2 border-dashed p-8 text-center transition-colors"
    :class="
      dragging
        ? 'border-sky-500 bg-sky-50 dark:bg-sky-950/40'
        : 'border-slate-300 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
    "
    @dragenter="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <svg
      class="mx-auto mb-3 h-10 w-10 text-slate-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="1.5"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-.41-8.98 4.5 4.5 0 0 1 8.08-3.02A4.5 4.5 0 0 1 21 10.5a4.5 4.5 0 0 1-4.5 4.5H6.75Z"
      />
    </svg>

    <p class="text-sm font-medium">Drop documents here</p>
    <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
      Word · PowerPoint · Excel · OpenDocument · RTF · EPUB · CSV · PDF
    </p>

    <button
      type="button"
      class="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
      @click="input?.click()"
    >
      Choose files
    </button>

    <input
      ref="input"
      type="file"
      class="sr-only"
      multiple
      :accept="ACCEPT_ATTRIBUTE"
      @change="onPick"
    />

    <p class="mt-4 text-xs text-slate-500 dark:text-slate-400">
      Converted in your browser — nothing is uploaded.
    </p>
  </div>
</template>
